import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { CircleCheck, Info } from "lucide-react";
import { cn } from "@nasam/ui";

export interface PasswordRequirement {
  readonly key: string;
  readonly label: string;
  readonly met: boolean;
}

/**
 * The "create/reset password" screens (Figma nodes 2676:427210 and
 * 2676:431966) list the same five password rules as bullet points with an
 * info icon that Figma renders statically. Here the icon reflects whether
 * the rule is currently satisfied -- an info glyph while unmet, a check
 * once satisfied -- since the password is otherwise typed blind. Shared by
 * the auth screens and the profile's change-password screen.
 */
export function PasswordRequirementsList({
  requirements,
  className,
}: {
  readonly requirements: readonly PasswordRequirement[];
  readonly className?: string;
}) {
  return (
    <ul
      className={cn(
        "flex w-full flex-wrap items-center justify-center gap-x-6 gap-y-2",
        className,
      )}
    >
      {requirements.map((requirement) => (
        <li
          key={requirement.key}
          data-met={requirement.met ? "true" : "false"}
          className={cn(
            "flex items-center gap-2 text-body-l-r",
            requirement.met ? "text-text" : "text-text-placeholder",
          )}
        >
          {requirement.met ? (
            <CircleCheck
              className="size-4 shrink-0 text-badge-text-green"
              aria-hidden="true"
            />
          ) : (
            <Info
              className="size-4 shrink-0 text-secondary-icon"
              aria-hidden="true"
            />
          )}
          {requirement.label}
        </li>
      ))}
    </ul>
  );
}

export function usePasswordRequirements(
  password: string,
): readonly PasswordRequirement[] {
  const { t } = useTranslation();

  return useMemo(
    () => [
      {
        key: "minLength",
        label: t("authentication:passwordReqMinLength"),
        met: password.length >= 8,
      },
      {
        key: "uppercase",
        label: t("authentication:passwordReqUppercase"),
        met: /[A-Z]/.test(password),
      },
      {
        key: "lowercase",
        label: t("authentication:passwordReqLowercase"),
        met: /[a-z]/.test(password),
      },
      {
        key: "number",
        label: t("authentication:passwordReqNumber"),
        met: /[0-9]/.test(password),
      },
      {
        key: "specialChar",
        label: t("authentication:passwordReqSpecialChar"),
        met: /[^A-Za-z0-9]/.test(password),
      },
    ],
    [password, t],
  );
}
